// eslint-disable-next-line @nx/enforce-module-boundaries -- Shared renderer-independent row focus and theme foundation.
import {useDataGridRowFocus, getDataGridFocusTheme, EntityCommentDialog, type EntityCommentTarget, type DataGridFocusResult} from "@ots-frontend/ots-ui-tw";
import {CompactSelection, DataEditorProps, DataEditorRef, Item, Item as GridItem} from "@glideapps/glide-data-grid";
import {
  MessageEntity,
  ShiftEntity,
  TransportAssocEntity,
  TransportCategory,
  transportType
} from "@ots-frontend/ots-data";
import React, {forwardRef, useCallback, useEffect, useRef, useState, useMemo} from "react";
import {createPortal} from "react-dom";
import {CellClickedEventArgs} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-types";
import {useLayer} from "react-laag";
import {Group, Menu, useMantineTheme} from "@mantine/core";
import {getDataGridSeparatorTheme, getDataGridStatusRowTheme, useOtsColorScheme} from "../DataGrid/dataGridTheme";
import {IconCheckbox, IconEye, IconPlus, IconMessagePlus} from "@tabler/icons-react";
import {Theme} from "@glideapps/glide-data-grid/dist/ts/common/styles";
import {useTranslation} from "react-i18next";
import {isEqual} from "lodash";
import {GridRowContextMenu, isSizedGridColumn} from "../DataGrid/useDataGrid";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {TicketAction, TicketActionMenuDropdown} from "../../menu/TicketActionMenu/TicketActionMenu";
import {useTicketDataGrid} from "./useTicketDataGrid";
import {ThemedDataGrid} from "../DataGrid/ThemedDataGrid";
import {TransportActionMenuDropdown} from "../../menu/TransportActionMenu/TransportActionMenu";
import {EntityDataGridProps} from "../EntityDataGrid/EntityDataGrid";
import {CommentListModal} from "../../comment/CommentList/CommentList";
import {DataGridColumnPicker} from "../DataGrid/DataGridColumnPicker";
import {getDefaultTicketColumnIds} from "./ticketDataGridColumns";
import {filterDataGridRecordSelection} from "../DataGrid/dataGridSeparators";
import {DataGridSeparatorPicker} from "../DataGrid/DataGridSeparatorPicker";
import {TICKET_SEPARATOR_OPTIONS, TicketSeparatorMode} from "./ticketDataGridSeparators";

export type TicketDataGridProps = Partial<EntityDataGridProps<TransportAssocEntity>> & {
  onItemClick?: (item: TicketDataGridContextMenuItem, data: TransportAssocEntity[]) => void;
  category?: TransportCategory;
  columnStorageKey?: string;
  defaultSeparatorMode?: TicketSeparatorMode;
  shifts?: ShiftEntity[];
  appDttm?: number;
  /** Mount controls in a host toolbar. Pass null while its container mounts; omit for inline controls. */
  controlsTarget?: HTMLElement | null;
  focusPersonId?: string;
  onCommentCreated?: () => void;
  onFocusResolved?: (result: DataGridFocusResult) => void;
}

export const TicketDataGrid = forwardRef<DataEditorRef, TicketDataGridProps>((props, ref) => {
  const {
    data = [],
    gridSelection,
    onItemClick,
    onCellClicked,
    colWidths,
    onColumnResize,
    sort,
    onSort,
    category,
    columnStorageKey,
    defaultSeparatorMode,
    shifts,
    appDttm,
    controlsTarget,
    focusPersonId,
    onFocusResolved,
    onCommentCreated,
    height = "100%",
    onGridSelectionChange,
    ...rest
  } = props;

  const inheritedScheme = useOtsColorScheme();
  const appTheme = useMantineTheme();
  const colorScheme = inheritedScheme ?? appTheme.colorScheme;

  const auth = useAuth();
  const [commentTarget, setCommentTarget] = useState<EntityCommentTarget>();
  const [messages, setMessages] = useState<MessageEntity[] | undefined>(undefined);
  const [contextMenu, setContextMenu] = useState<GridRowContextMenu<TransportAssocEntity>>();

  const {columnLayout, separatorControls, rowOrder, getSeparator, ...dataGridArgs} = useTicketDataGrid({
    data: data,
    colWidths: colWidths,
    category: category,
    sort: sort,
    columnStorageKey,
    defaultSeparatorMode,
    shifts,
    appDttm,
    colorScheme,
  });

  const {
    hoverRow,
    getOriginalIndex,
    onColumnResize: persistColumnResize,
  } = dataGridArgs;

  const focusedRecordAt = useCallback((row: number) => data[getOriginalIndex(row)], [data, getOriginalIndex]);
  const matchesFocusedDriver = useCallback((ticket: TransportAssocEntity) =>
    ticket.pickupTransport?.personId === focusPersonId || ticket.deliveryTransport?.personId === focusPersonId,
    [focusPersonId]);
  const focusedRows = useDataGridRowFocus({rowCount: dataGridArgs.rows, recordAt: focusedRecordAt,
    matches: matchesFocusedDriver, focusKey: focusPersonId, onResolved: onFocusResolved});
  const focusTheme = useMemo(() => getDataGridFocusTheme(colorScheme), [colorScheme]);

  const resizeColumn = useCallback<NonNullable<DataEditorProps["onColumnResize"]>>((column, width, index, widthWithGrow) => {
    persistColumnResize?.(column, width, index, widthWithGrow);
    // The existing page stores an array in default order; never feed it reordered visible indices.
    onColumnResize?.(getDefaultTicketColumnIds(category).map(id => {
      const current = columnLayout.columns.find(candidate => candidate.id === id);
      return id === column.id ? width : current && isSizedGridColumn(current) ? current.width : undefined;
    }));
  }, [persistColumnResize, onColumnResize, category, columnLayout.columns]);

  const columnIds = dataGridArgs.columns.map(column => column.id).join(",");
  const previousColumnIds = useRef(columnIds);
  const previousRowOrder = useRef(rowOrder);
  useEffect(() => {
    const rowsChanged = previousRowOrder.current !== rowOrder;
    if (previousColumnIds.current !== columnIds || rowsChanged) {
      if (gridSelection) onGridSelectionChange?.({...gridSelection, rows: rowsChanged ? CompactSelection.empty() : gridSelection.rows, columns: CompactSelection.empty(), current: undefined});
      setContextMenu(undefined);
    }
    previousColumnIds.current = columnIds;
    previousRowOrder.current = rowOrder;
  }, [columnIds, rowOrder, gridSelection, onGridSelectionChange]);

  const handleGridSelectionChange = useCallback<NonNullable<DataEditorProps["onGridSelectionChange"]>>(selection => {
    onGridSelectionChange?.(filterDataGridRecordSelection(selection, row => getOriginalIndex(row) >= 0));
  }, [getOriginalIndex, onGridSelectionChange]);

  const isOpen = typeof contextMenu !== "undefined";

  const {layerProps, renderLayer} = useLayer({
    isOpen: isOpen,
    auto: true,
    placement: "bottom-end",
    triggerOffset: 16,
    containerOffset: 16,
    layerDimensions: layerSide => ({width: 200, height: contextMenu?.possibleHeight ?? 315}),
    onOutsideClick: () => setContextMenu(undefined),
    trigger: {
      getBounds: () => ({
        left: contextMenu?.bounds.x ?? 0,
        top: contextMenu?.bounds.y ?? 0,
        width: contextMenu?.bounds.width ?? 0,
        height: contextMenu?.bounds.height ?? 0,
        right: (contextMenu?.bounds.x ?? 0) + (contextMenu?.bounds.width ?? 0),
        bottom: (contextMenu?.bounds.y ?? 0) + (contextMenu?.bounds.height ?? 0)
      })
    }
  });

  const onCellContextMenu = useCallback((cell: GridItem, event: CellClickedEventArgs) => {
    event.preventDefault();

    const rootRow = getOriginalIndex(cell[1]);
    if (rootRow < 0) return;

    if (gridSelection && gridSelection.rows.toArray().includes(cell[1])) {
      const selectedTtas = gridSelection.rows.toArray()
        .map((row) => data[getOriginalIndex(row)])
        .filter((ticket): ticket is TransportAssocEntity => !!ticket);

      if (selectedTtas.length === 0) {
        return;
      }

      setContextMenu({
        col: cell[0],
        row: rootRow,
        bounds: event.bounds,
        data: selectedTtas,
        possibleHeight: 165,
      });
    } else {
      const tta = data[rootRow];
      if (!tta) {
        return;
      }
      setContextMenu({
        col: cell[0],
        row: rootRow,
        bounds: event.bounds,
        data: [tta],
        possibleHeight: tta.pickupTransportId && tta.deliveryTransportId ? 315 : 165,
      });
    }
  }, [data, getOriginalIndex, gridSelection]);

  const onContextMenuItemClick = useCallback((item: TicketDataGridContextMenuItem, data: TransportAssocEntity[]) => {
    setContextMenu(undefined);
    if ((item === "COMMENT" || item === "COMMENT_DELIVERY") && data.length === 1 && auth?.hasAccess("transport_edit")) {
      const ticket = data[0];
      const transport = item === "COMMENT_DELIVERY" ? ticket.deliveryTransport : ticket.pickupTransport ?? ticket.deliveryTransport;
      const id = transport?.id ?? (item === "COMMENT_DELIVERY" ? ticket.deliveryTransportId : ticket.pickupTransportId ?? ticket.deliveryTransportId);
      if (id) setCommentTarget({objectId: id, objectType: "TRANSPORT", tenantCode: ticket.database,
        label: `${item === "COMMENT_DELIVERY" || !ticket.pickupTransportId ? "delivery" : "pickup"} ticket ${ticket.resourceIdentifiers.join(", ")}`});
    } else onItemClick?.(item, data);
  }, [onItemClick, auth]);

  const getRowThemeOverride = useCallback((row: number): Partial<Theme> | undefined => {
    if (getSeparator(row)) return getDataGridSeparatorTheme(colorScheme);
    if (focusedRows.has(row)) return focusTheme;
    const tta = data[getOriginalIndex(row)];
    const tone = tta?.deliveryTransport && tta.pickupTransport ? "success" : tta?.pickupTransport ? "warning" : "neutral";
    return getDataGridStatusRowTheme(tone, colorScheme, hoverRow === row);
  }, [data, hoverRow, getOriginalIndex, getSeparator, colorScheme, focusedRows, focusTheme]);

  const _onCellClicked = useCallback((cell: Item, event: CellClickedEventArgs) => {
    if (getOriginalIndex(cell[1]) < 0) return;
    if (dataGridArgs.columns[cell[0]]?.id === "STATUS") {
      const tta = data[getOriginalIndex(cell[1])];
      if (tta && tta.messages && tta.messages.length > 0) {
        setMessages(tta.messages)
      }
    }

    if (onCellClicked) {
      onCellClicked(cell, event)
    }
  }, [data, getOriginalIndex, onCellClicked, dataGridArgs.columns])

  useEffect(() => {
    if (onSort && separatorControls.mode === "NONE") {
      onSort(dataGridArgs.sort)
    }
  }, [onSort, dataGridArgs.sort, separatorControls.mode])

  const controls = (
    <Group position="right" mb={controlsTarget === undefined ? "xs" : 0}>
      <DataGridSeparatorPicker mode={separatorControls.mode} onChange={separatorControls.setMode} options={TICKET_SEPARATOR_OPTIONS}/>
      <DataGridColumnPicker {...columnLayout}/>
    </Group>
  );

  return (
    <>
      <div style={{display: "flex", flexDirection: "column", height, minHeight: 0}}>
        {controlsTarget === undefined ? controls : controlsTarget ? createPortal(controls, controlsTarget) : null}
        <div style={{flex: 1, minHeight: 0}}>
          <ThemedDataGrid
            colorScheme={colorScheme}
            key={category?.code ?? "standard"}
            {...rest}
            {...dataGridArgs}
            height="100%"
            onColumnResize={resizeColumn}
            ref={ref}
            width="100%"
            smoothScrollX={true}
            smoothScrollY={true}
            rowMarkers="both"
            getCellsForSelection={true}
            theme={{
              cellVerticalPadding: 8
            }}
            onCellClicked={_onCellClicked}
            getRowThemeOverride={getRowThemeOverride}
            onCellContextMenu={onCellContextMenu}
            gridSelection={gridSelection}
            onGridSelectionChange={handleGridSelectionChange}
            rowSelect="multi"
            rowSelectionMode="multi"/>
        </div>
      </div>
      {isOpen &&
        renderLayer(
          <div {...layerProps}>
            <ContextMenu
              {...contextMenu}
              onItemClick={onContextMenuItemClick}/>
          </div>
        )
      }

      {commentTarget && <EntityCommentDialog key={commentTarget.objectId} target={commentTarget} onCreated={onCommentCreated} onClose={() => setCommentTarget(undefined)}/>}
      {messages && messages.length > 0 &&
        <CommentListModal
          listProps={{
            comments: messages,
          }}
          withinPortal={true}
          centered={true}
          mih={400}
          opened={true}
          onClose={() => setMessages(undefined)}
        />
      }
    </>
  );
});

export type TicketDataGridContextMenuItem = TicketAction |
  "COMMENT" |
  "COMMENT_DELIVERY" |
  "VIEW" |
  "VIEW_DISPATCH" |
  "NEW_DISPATCH" |
  "ADD_TRANSPORT" |
  "CLEAR" |
  "DISPATCH_VALIDATE" |
  "DISPATCH_INVALIDATE";

type ContextMenuProps = {
  data: TransportAssocEntity[]
  onItemClick: (item: TicketDataGridContextMenuItem, data: TransportAssocEntity[]) => void
}

const ContextMenu = (props: ContextMenuProps) => {
  const {data, onItemClick} = props;

  const {t} = useTranslation(["common"]);

  // no data
  if (data.length === 0) {
    return undefined
  }

  // multi selection
  if (data.length > 1) {
    return (
      <Menu shadow="md" width={200} opened={true}>
        <Menu.Dropdown>
          <HasAccess permissions={["transporttransportassoc_edit"]}>
            <Menu.Item
              onClick={() => onItemClick("DISPATCH_VALIDATE", data)}
            >
              {t("common.action.invalidate", "Validate")}
            </Menu.Item>
            <Menu.Item
              onClick={() => onItemClick("DISPATCH_INVALIDATE", data)}
              >
              {t("common.action.validate", "Invalidate")}
            </Menu.Item>
          </HasAccess>
          <Menu.Divider />
          <Menu.Item
            onClick={() => onItemClick("CLEAR", data)}>
            {t("common.action.clear", "Clear")}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    )
  }

  // single selection
  const item = data[0]
  const transport = item.pickupTransport ?? item.deliveryTransport

  return (
    <Menu shadow="md" width={250} opened={true}>
      <Menu.Dropdown>
        <Menu.Label>{item.resourceIdentifiers.join(", ")}</Menu.Label>
        <HasAccess permissions={["transport_edit"]}>
          {(item.pickupTransportId || item.deliveryTransportId) && <Menu.Item icon={<IconMessagePlus size={14}/>} onClick={() => onItemClick("COMMENT", data)}>
            {item.pickupTransportId && item.deliveryTransportId ? "Add pickup comment" : "Add comment"}
          </Menu.Item>}
          {item.pickupTransportId && item.deliveryTransportId && <Menu.Item icon={<IconMessagePlus size={14}/>} onClick={() => onItemClick("COMMENT_DELIVERY", data)}>Add delivery comment</Menu.Item>}
        </HasAccess>
        {(item.pickupTransportId && item.deliveryTransportId) ? (
            <>
              <Menu.Item
                icon={<IconEye size={14}/>}
                onClick={() => onItemClick("VIEW", data)}>
                {t("common.action.view", "View")}
              </Menu.Item>
              <TicketActionMenuDropdown
                ticket={item}
                onActionClick={(action) => onItemClick(action, data)}
                showEmpty={false}/>
              <Menu.Divider/>
              <HasAccess permissions={["dispatch_view"]}>
                {item.dispatchId && (
                  <Menu.Item
                    icon={<IconCheckbox size={14}/>}
                    onClick={() => onItemClick("VIEW_DISPATCH", data)}>
                    {t("common.action.view.dispatch")}
                  </Menu.Item>
                )}
              </HasAccess>
              <HasAccess permissions={["dispatch_create"]}>
                <Menu.Item
                  icon={!item.dispatchId ? <IconCheckbox size={14}/> : undefined}
                  onClick={() => onItemClick("NEW_DISPATCH", data)}>
                  {t("common.action.new.dispatch")}
                </Menu.Item>
              </HasAccess>
            </>
          ) :
          <>
            {transport &&
              <>
                <Menu.Item
                  icon={<IconPlus size={14}/>}
                  onClick={() => onItemClick("ADD_TRANSPORT", data)}>
                  {t("common.action.add", "Add") + " " + (transport.type === transportType.pickup ? t(transportType.delivery.displayValue.id, transportType.delivery.displayValue.defaultMessage) : t(transportType.pickup.displayValue.id, transportType.pickup.displayValue.defaultMessage))}
                </Menu.Item>
                <TransportActionMenuDropdown
                  transport={transport}
                  showEmpty={false}/>
              </>
            }
          </>
        }
      </Menu.Dropdown>
    </Menu>
  );
};

export const TicketDataGridMemo = React.memo(
  TicketDataGrid,
  isEqual
);
